// Utilidades de datetime ancladas a una zona horaria configurada.
// Modelo: el instante se almacena (ISO). Display y entrada se anclan a la TZ
// configurada (default VITE_DEFAULT_TIMEZONE / "America/Mexico_City"), con override
// por atributo via morphedInputTypeOptions [{option:"timezone", value:"America/Mexico_City"}].
// No depende de la TZ de la computadora del cliente.

const DEFAULT_TZ: string =
	(import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DEFAULT_TIMEZONE ||
	"America/Mexico_City";

interface OptAttr {
	morphedInputTypeOptions?: Array<{ option?: string; value?: unknown }> | null;
}

export function resolveTimezone(attr?: OptAttr | null): string {
	const opts = attr?.morphedInputTypeOptions;
	if (Array.isArray(opts)) {
		for (const o of opts) {
			if (o && o.option === "timezone" && typeof o.value === "string" && o.value.trim()) {
				return o.value.trim();
			}
		}
	}
	return DEFAULT_TZ;
}

interface TZParts { year: string; month: string; day: string; hour: string; minute: string; }

function partsInTZ(d: Date, tz: string): TZParts {
	const f = new Intl.DateTimeFormat("en-US", {
		timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
		hour: "2-digit", minute: "2-digit", hour12: false,
	});
	const parts = f.formatToParts(d);
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
	let hour = get("hour");
	if (hour === "24") hour = "00"; // some environments emit 24 for midnight
	return { year: get("year"), month: get("month"), day: get("day"), hour, minute: get("minute") };
}

/** Offset de la TZ (en minutos) para un instante dado. ej. Mexico UTC-6 → -360. */
function tzOffsetMinutes(d: Date, tz: string): number {
	try {
		const f = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" });
		const tzName = f.formatToParts(d).find((p) => p.type === "timeZoneName")?.value ?? "";
		const m = tzName.match(/GMT([+-]?\d{1,2})(?::(\d{2}))?/i);
		if (!m) return 0;
		const raw = m[1];
		const sign = raw.startsWith("-") ? -1 : 1;
		const h = Math.abs(parseInt(raw, 10) || 0);
		const min = m[2] ? parseInt(m[2], 10) : 0;
		return sign * (h * 60 + min);
	} catch {
		return 0;
	}
}

/** Instante ISO → wall-clock en la TZ configurada, en el formato del input. */
export function toInputValue(value: unknown, attr?: OptAttr | null, inputType?: string): string {
	if (!value) return "";
	const d = new Date(String(value));
	if (isNaN(d.getTime())) return "";
	const tz = resolveTimezone(attr);
	const p = partsInTZ(d, tz);
	if (inputType === "date") return `${p.year}-${p.month}-${p.day}`;
	if (inputType === "time") return `${p.hour}:${p.minute}`;
	return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Wall-clock en la TZ configurada (valor del input) → instante ISO con offset. */
export function fromInputValue(localStr: string, attr?: OptAttr | null, inputType?: string): string | null {
	if (!localStr) return null;
	const tz = resolveTimezone(attr);
	let reference: Date;
	if (inputType === "date") {
		reference = new Date(`${localStr}T00:00:00Z`);
	} else if (inputType === "time") {
		// time-only: no hay fecha; devolver HH:MM tal cual (sin contexto de instante)
		return localStr;
	} else {
		reference = new Date(`${localStr}:00Z`);
	}
	if (isNaN(reference.getTime())) return localStr;
	const offsetMin = tzOffsetMinutes(reference, tz);
	const real = new Date(reference.getTime() - offsetMin * 60000);
	return real.toISOString();
}
